import './instrument'
import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import * as Sentry from '@sentry/react'
import { BrowserRouter } from 'react-router-dom'
// Workspace design system: IBM Plex Sans for UI, IBM Plex Mono for metadata
// (counts, timestamps, shortcuts, transliteration), Heebo for Hebrew.
import '@fontsource/ibm-plex-sans/400.css'
import '@fontsource/ibm-plex-sans/500.css'
import '@fontsource/ibm-plex-sans/600.css'
import '@fontsource/ibm-plex-mono/400.css'
import '@fontsource/ibm-plex-mono/500.css'
import '@fontsource-variable/heebo'
import { Toaster } from 'sonner'

import App from './App'
import ConsentBanner from './components/ConsentBanner'
import { ThemeProvider } from './theme-provider'
import { LocaleProvider } from '@baba/i18n/react'
import { store } from './store/store'
import { babaApi } from './store/babaApi'
import { registerSubscriptionInvalidation } from './store/subscriptionInvalidation'
import { loadCrisp } from '@/lib/crisp'
import { getGoogleAdsConfig } from '@/lib/google-ads-env'
import { whenConsented } from '@baba/attribution/ad-pixels'
import { initPostHog } from '@/lib/posthog'
import { getAuthEmail, isSignedIn } from '@/lib/baba-auth'
import { reconcileAccountLocalState, reloadWhenAccountOwnerChanges } from '@/lib/accountLocalState'
import { readCachedSubscription } from '@/lib/baba-api'
import './globals.css'

// Before anything reads localStorage: chats, recents and caches recorded for
// a different account than the one signed in now are cleared, so this
// session neither shows them nor syncs them into its own account.
reconcileAccountLocalState(isSignedIn() ? getAuthEmail() : null)

// Another tab signed in, signed out or switched accounts: what this tab holds
// in memory was loaded for the previous owner, so it starts over from the
// reconciled state. Every change counts, guest to account included.
reloadWhenAccountOwnerChanges(window, () => window.location.reload())

// The plan, from the last answer this account got, so the rail, the More
// sheet and the paywall do not start from "Free" for a Pro member while the
// status request is in flight. The request still runs now, and its answer
// replaces the seed.
if (isSignedIn()) {
  const cached = readCachedSubscription()
  if (cached && !cached.authExpired) {
    store.dispatch(babaApi.util.upsertQueryEntries([{ endpointName: 'subscriptionStatus', arg: undefined, value: cached }]))
  }
  void store.dispatch(babaApi.endpoints.subscriptionStatus.initiate(undefined, { forceRefetch: true, subscribe: false }))
}

/** Last resort, outside the router's pages: plain links, no app state. */
function RootErrorFallback() {
  return (
    <div role="alert" style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center' }}>
      <p style={{ fontSize: 17, fontWeight: 600 }}>Something went wrong.</p>
      <p style={{ fontSize: 14, opacity: 0.75 }}>We've been notified and are looking into it.</p>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="button" onClick={() => window.location.reload()} style={{ height: 40, padding: '0 20px', borderRadius: 999, fontWeight: 600, background: '#c2703a', color: '#fff', cursor: 'pointer' }}>
          Reload
        </button>
        <a href="/" style={{ height: 40, padding: '0 20px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', border: '1px solid currentColor', fontWeight: 500 }}>
          Home
        </a>
      </div>
    </div>
  )
}

function SubscriptionCacheBridge() {
  useEffect(() => registerSubscriptionInvalidation(store.dispatch), [])
  return null
}

// Google Ads tag — appended at runtime so the AW- id comes from build-time env vars
// rather than living hard-coded in index.html. Reads either VITE_* or NEXT_PUBLIC_* vars.
const { googleAdsId: GOOGLE_ADS_ID } = getGoogleAdsConfig()
if (GOOGLE_ADS_ID) {
  const w = window as unknown as { gtag?: (...a: unknown[]) => void }
  w.gtag?.('config', GOOGLE_ADS_ID, { send_page_view: false })
}

// Defer PostHog init and Crisp injection until the browser is idle. Both are
// heavy on the critical path and block LCP on mobile if they run during the
// first render burst. Events fired before PostHog is up are queued by
// lib/posthog.ts and replayed on load, so nothing is lost to the delay.
const deferredBoot = () => {
  // PostHog sets a cookie: it waits for analytics_storage (EEA, UK and CH: until the visitor accepts).
  whenConsented('analytics_storage', () => void initPostHog())
  loadCrisp()
}

type IdleWindow = typeof window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
}
const w = window as IdleWindow
if (typeof w.requestIdleCallback === 'function') {
  w.requestIdleCallback(deferredBoot, { timeout: 2500 })
} else {
  setTimeout(deferredBoot, 1200)
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Provider store={store}>
    <BrowserRouter>
      <ThemeProvider>
       <LocaleProvider>
        <SubscriptionCacheBridge />
        {/* Before App: sonner's Toaster only hears toasts raised after its own
            mount effect, and effects run in tree order, so a toast the shell
            raises while mounting (a sign-in hand-off that failed, the
            checkout welcome, a confirmed email change) was dropped when the
            Toaster came after it. */}
        <Toaster
          theme="system"
          position="bottom-right"
          toastOptions={{
            style: {
              background: 'var(--surface)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text)',
              boxShadow: 'var(--shadow-lg)',
              borderRadius: '16px',
            },
          }}
        />
        <Sentry.ErrorBoundary fallback={<RootErrorFallback />} showDialog>
        <App />
        </Sentry.ErrorBoundary>
        <ConsentBanner />
       </LocaleProvider>
      </ThemeProvider>
    </BrowserRouter>
    </Provider>
  </StrictMode>,
)
